import React, {useState, useEffect, useMemo} from 'react'

import { AlertIcon, CircleSlashIcon, DuplicateIcon, CheckCircleIcon } from '@primer/octicons-react'
import { useDropzone } from 'react-dropzone'
import axios from 'axios'
import Button from 'react-bootstrap/Button'
import CardColumns from 'react-bootstrap/CardColumns'
import ListGroup from 'react-bootstrap/ListGroup'
import Swal from 'sweetalert2'
import ProgressBar from 'react-bootstrap/ProgressBar'

import { REST_API, UPLOADS } from './../../../../../services/constants'
import authHeader from '../../../../../services/auth-header'
import FileService from './../../../../../services/file-service'
import UploadedFile from './../../../../../components/uploads/UploadedFile'

export default function FileManager(props) {
    const [ uploadedFiles, setUploadedFiles ] = useState([]);

    const updateUploadedFiles = async () => {
        axios({
            method: 'GET',
            url: `${REST_API}/api/secure/cloud/files/uploaded_files/`,
            headers: await authHeader(),
            params: {
                taskId: props.taskId
            }
        }).then(response => {
            if (response.status === 200) {
                setUploadedFiles(response.data);
            }
        });
    }

    const deleteUploadedFile = async (fileName) => {
        axios({
            method: 'DELETE',
            url: `${REST_API}/api/secure/cloud/files/uploaded_files/`,
            headers: await authHeader(),
            params: {
                taskId: props.taskId,
                fileName: fileName
            }
        }).then(response => {
            if (response.status === 200) {
                Toast.fire({
                    icon: 'success',
                    title: 'Gelöscht'
                });

                updateUploadedFiles();
            }
        }).catch(err => {
            if (err.response && err.response.status === 404) {
                Swal.fire({
                    icon: 'error',
                    title: 'Löschen fehlgeschlagen',
                    text: 'Das Löschen ist fehlgeschlagen. Bitte versuche es erneut oder kontaktiere einen Administrator'
                });
            }
        });
    }

    const {
        acceptedFiles,
        fileRejections,
        isDragActive,
        isDragAccept,
        isDragReject,
        getRootProps,
        getInputProps
    } = useDropzone({
        accept: UPLOADS.TASK_CONTENT.ALLOWED_EXTENSIONS
    });

    const baseStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        borderWidth: 2,
        borderRadius: 2,
        borderColor: '#eeeeee',
        borderStyle: 'dashed',
        color: '#bdbdbd',
        outline: 'none',
        transition: 'border .24s ease-in-out'
    };
      
    const activeStyle = {
        borderColor: '#2196f3'
    };
      
    const acceptStyle = {
        borderColor: '#00e676'
    };
      
    const rejectStyle = {
        borderColor: '#ff1744'
    };

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [isDragActive, isDragReject, isDragAccept]);

    const showDeleteFileModal = (file) => {
        Swal.fire({
            icon: "warning",
            title: "Datei löschen?",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            confirmButtonAriaLabel: `danger-button`,
            text: "Sicher, dass du die Datei löschen möchtest?"
        }).then(result => {
            if (result.isConfirmed) {
                deleteUploadedFile(file.name);
            }
        });
    }

    useEffect(() => {
        updateUploadedFiles();
    }, []);

    useEffect(() => {
        acceptedFiles.forEach(x => {
            setUploadingFiles([...uploadingFiles, x]);
        });
    }, [acceptedFiles]);

    const [ uploadingFiles, setUploadingFiles ] = useState([]);

    return (
        <>
        {
            props.taskId !== null && props.taskId !== undefined ?
            <div style={{minHeight: 100}}>
                <section className="file-upload-dropzone">
                    <div {...getRootProps({style})}>
                        <input {...getInputProps()} />
                        <p>Ziehe eine oder mehrere Dateien hierher per Drag and Drop oder klicke, um Dateien auszuwählen</p>
                        <em>(Es werden nur {UPLOADS.TASK_CONTENT.ALLOWED_EXTENSIONS} akzeptiert)</em>
                    </div>
                    <aside>
                        {
                            fileRejections !== null && fileRejections !== undefined && fileRejections.length > 0 ?
                            <ListGroup className="mt-3">
                                {
                                    fileRejections.map((x, index) => {
                                        return (
                                            <ListGroup.Item key={index} className="bg-warning">
                                                <AlertIcon className="mr-2" />{x.file.name} kann nicht hochgeladen werden.
                                                <ul style={{paddingLeft: 20, paddingTop: 10}}>
                                                    {
                                                        x.errors.map((error, index) => {
                                                            return <li key={index}>
                                                                {error.message}
                                                            </li>
                                                        })
                                                    }
                                                </ul>
                                            </ListGroup.Item>
                                        )
                                    })
                                }
                            </ListGroup>
                            : null
                        }

                        {
                            acceptedFiles !== null && acceptedFiles !== undefined && acceptedFiles.length > 0 ?
                            <ListGroup className="mt-3">
                                {
                                    uploadingFiles.map((uploadingFile, index) => {
                                        return <FileUploadProgress key={index} data={uploadingFile} taskId={props.taskId} onUploadFinished={() => updateUploadedFiles()} />
                                    })
                                }
                            </ListGroup>
                            : null
                        }
                    </aside>
                </section>

                <CardColumns className="mt-3">
                    {
                        uploadedFiles.map((file, index) => {
                            return <UploadedFile key={index} data={file} deleteCallback={() => showDeleteFileModal(file)} editable />
                        })
                    }
                </CardColumns>
            </div>
            : null
        } 
        </>
    )
}

function FileUploadProgress (props) {
    const fileName = props.data.name;
    const totalFileSize = props.data.size;

    const [ uploadProgress, setUploadProgress ] = useState({
        progress: 0,
        bytesUploaded: 0,
        totalBytes: totalFileSize,
        finished: false
    });

    const [ hideControls, setHideControls ] = useState(false);

    const [ cancelled, setCancelled ] = useState(false);
    const [ cancelToken ] = useState(axios.CancelToken.source());

    const [ errorDedected, setErrorDedected ] = useState(false);
    const [ fileAlreadyExists, setFileAlreadyExists ] = useState(false);

    const startFileUpload = async (file, onUploadProgress) => {
        let formData = new FormData();
        formData.append("file", file);

        return axios.post(`${REST_API}/api/secure/cloud/files/uploaded_files/`, formData, {
            cancelToken: cancelToken.token,
            headers: {
                ...await authHeader(),
                "Content-Type": "multipart/form-data"
            },
            params: {
                topicId: props.taskId
            },
            onUploadProgress
        }).then(response => {
            if (response && response.status === 200) {
                setUploadProgress({...uploadProgress, finished: true, bytesUploaded: totalFileSize});

                if (props.onUploadFinished) {
                    props.onUploadFinished();
                }
            }
        }).catch(error => {
            if (error.response) {
                if (error.response.status === 304) {
                    setFileAlreadyExists(true);
                    setHideControls(true);
                } else {
                    setErrorDedected(true);
                }
            }
        });
    }

    const cancelFileUpload = () => {
        cancelToken.cancel();
        setHideControls(true);
        setCancelled(true);
    }

    useEffect(() => {
        startFileUpload(props.data, event => {
            const progress = Math.round((100 * event.loaded) / event.total);
            const bytesUploaded = event.loaded;

            setUploadProgress({...uploadProgress, progress: progress, bytesUploaded: bytesUploaded});
        });

        return () => {
            cancelFileUpload();
        }
    }, [])

    return (
        <>
            <ListGroup.Item>
                <p className="mb-0">
                    {uploadProgress.finished ?
                        <span className="text-success mr-2"><CheckCircleIcon className="mr-2" />Abgeschlossen</span>
                    : null}
                    {fileAlreadyExists ?
                        <span className="text-danger mr-2"><DuplicateIcon className="mr-2" />Existiert bereits</span>
                    : null}
                    {errorDedected ? 
                        <span className="text-danger mr-2"><AlertIcon className="mr-2" />Fehler</span>
                    : null}
                    {cancelled ?
                        <span className="text-danger mr-2"><CircleSlashIcon className="mr-2" />Abgebrochen</span>
                    : null}
                    {fileName} ({FileService.fileSizeToHumanReadableFormat(uploadProgress.bytesUploaded)} / {FileService.fileSizeToHumanReadableFormat(totalFileSize)})</p>
                
                {!uploadProgress.finished && !errorDedected && !cancelled && !fileAlreadyExists ?
                    <ProgressBar
                        animated
                        variant="primary"
                        now={uploadProgress.progress}
                        label={`${uploadProgress.progress}% (${FileService.fileSizeToHumanReadableFormat(uploadProgress.bytesUploaded)} B / ${FileService.fileSizeToHumanReadableFormat(uploadProgress.sizeInBytes)})`} />
                            
                : null}

                {
                    !uploadProgress.finished && !hideControls ?
                    <Button
                        className="mt-2"
                        variant="danger"
                        disabled={uploadProgress.finished}
                        onClick={() => cancelFileUpload()}>
                            <CircleSlashIcon className="mr-2"/>Abbrechen
                    </Button>
                    : null
                }
            </ListGroup.Item>
        </>
    )
}

const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
});